import { useState, useEffect, Component } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft, FileText, ClipboardCheck, Bell, Users,
  Calendar as CalendarIcon, Copy, Check, Trophy, Video, Award, AlertTriangle, Zap, RotateCcw, Swords
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { DocumentsTab } from "@/components/classDetail/DocumentsTab";
import { QuizzesTab } from "@/components/classDetail/QuizzesTab";
import { AnnouncementsTab } from "@/components/classDetail/AnnouncementsTab";
import { AssignmentsTab } from "@/components/classDetail/AssignmentsTab";
import { PracticeTab } from "@/components/classDetail/PracticeTab";
import { Gamepad2 } from "lucide-react";
import { CertificatesTab } from "@/components/classDetail/CertificatesTab";
import { JoinRequestsCard, StudentManagementCard } from "@/components/classDetail/StudentsTab";

class ClassDetailErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8 max-w-md">
            <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">Please try refreshing the page.</p>
            <Button onClick={() => window.location.reload()} className="rounded-full">Refresh Page</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


function ArenaTeacherTab({ classId, headers }) {
  const [assignedTables, setAssignedTables] = useState([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
  const [heatmap, setHeatmap] = useState(null);
  const [weeklyBoard, setWeeklyBoard] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Class Battle state
  const [activeBattles, setActiveBattles] = useState([]);
  const [battleHistory, setBattleHistory] = useState([]);
  const [battleTables, setBattleTables] = useState([2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
  const [battleTitle, setBattleTitle] = useState("");
  const [battleDuration, setBattleDuration] = useState(3);
  const [creating, setCreating] = useState(false);
  const [viewingBattle, setViewingBattle] = useState(null);

  const fetchData = async () => {
    try {
      const [aRes, hRes, wRes, abRes, bhRes] = await Promise.all([
        axios.get(`${API}/arena/assignments/${classId}`, { headers }),
        axios.get(`${API}/arena/heatmap/${classId}`, { headers }),
        axios.get(`${API}/arena/leaderboard/weekly/${classId}`, { headers }),
        axios.get(`${API}/arena/class-battle/active/${classId}`, { headers }),
        axios.get(`${API}/arena/class-battle/history/${classId}`, { headers })
      ]);
      setAssignedTables(aRes.data.tables || [2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20]);
      setHeatmap(hRes.data);
      setStudents(hRes.data.students || []);
      setWeeklyBoard(wRes.data.leaderboard || []);
      setActiveBattles(abRes.data || []);
      setBattleHistory(bhRes.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [classId]);

  const toggleTable = (t) => {
    setAssignedTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a,b) => a - b));
  };

  const toggleBattleTable = (t) => {
    setBattleTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort((a,b) => a - b));
  };

  const saveAssignment = async () => {
    try {
      await axios.post(`${API}/arena/assign`, { class_id: classId, tables: assignedTables }, { headers });
      toast.success("Tables assigned!");
    } catch { toast.error("Failed to assign"); }
  };

  const resetWeekly = async () => {
    if (!confirm("Reset the weekly Arena leaderboard for this class?")) return;
    try {
      await axios.post(`${API}/arena/leaderboard/weekly/reset/${classId}`, {}, { headers });
      setWeeklyBoard([]);
      toast.success("Weekly leaderboard reset!");
    } catch { toast.error("Failed to reset"); }
  };

  const createClassBattle = async () => {
    if (battleTables.length === 0) { toast.error("Select at least one table"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/arena/class-battle/create`, {
        class_id: classId,
        tables: battleTables,
        title: battleTitle || "Class Battle",
        duration_minutes: battleDuration
      }, { headers });
      toast.success("Class Battle created! Students can now join.");
      setBattleTitle("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    setCreating(false);
  };

  const endBattle = async (battleId) => {
    if (!confirm("End this battle? No more students can join.")) return;
    try {
      await axios.post(`${API}/arena/class-battle/${battleId}/end`, {}, { headers });
      toast.success("Battle ended");
      fetchData();
    } catch { toast.error("Failed to end battle"); }
  };

  const viewBattleResults = async (battleId) => {
    try {
      const res = await axios.get(`${API}/arena/class-battle/${battleId}`, { headers });
      setViewingBattle(res.data);
    } catch { toast.error("Failed to load results"); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // Viewing a battle's results
  if (viewingBattle) {
    const parts = viewingBattle.participants || [];
    return (
      <div className="space-y-4" data-testid="battle-results-view">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setViewingBattle(null)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <h3 className="font-bold">{viewingBattle.title}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${viewingBattle.status === "active" ? "bg-green-500/10 text-green-500" : "bg-muted text-muted-foreground"}`}>{viewingBattle.status}</span>
        </div>
        <p className="text-sm text-muted-foreground">{parts.length} participants &middot; Tables: {(viewingBattle.tables || []).join(", ")}x</p>
        {parts.length === 0 ? <p className="text-muted-foreground text-center py-6">No submissions yet</p> : (
          <div className="space-y-2">
            {parts.map((p, i) => (
              <div key={p.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-300" : i === 2 ? "bg-orange-400 text-white" : "bg-muted"}`}>{i + 1}</span>
                <div className="flex-1"><p className="font-medium text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{p.answered} answered &middot; {p.avg_time_ms ? `${(p.avg_time_ms/1000).toFixed(1)}s avg` : ""}</p></div>
                <p className="text-lg font-bold text-green-500">{p.score}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="arena-teacher-tab">
      {/* Create Class Battle */}
      <Card className="border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-pink-500/5">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Swords className="w-4 h-4 text-purple-500" />Create Class Battle</CardTitle>
          <CardDescription>Start a timed battle for all students in this class</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <input type="text" value={battleTitle} onChange={e => setBattleTitle(e.target.value)}
            placeholder="Battle title (e.g., Friday Speed Test)" data-testid="battle-title-input"
            className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm" />
          <div>
            <p className="text-xs text-muted-foreground mb-2">Select tables:</p>
            <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5">
              {[2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(t => (
                <button key={t} onClick={() => toggleBattleTable(t)} data-testid={`battle-table-${t}`}
                  className={`h-9 rounded-lg font-bold text-xs transition-all ${battleTables.includes(t) ? "bg-purple-500 text-white" : "bg-muted text-muted-foreground"}`}>{t}x</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Duration:</span>
            {[2, 3, 5].map(d => (
              <button key={d} onClick={() => setBattleDuration(d)} data-testid={`duration-${d}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${battleDuration === d ? "bg-purple-500 text-white" : "bg-muted text-muted-foreground"}`}>{d} min</button>
            ))}
          </div>
          <Button onClick={createClassBattle} disabled={creating || battleTables.length === 0}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:opacity-90" data-testid="create-class-battle-btn">
            <Swords className="w-4 h-4 mr-2" />{creating ? "Creating..." : "Start Class Battle"}
          </Button>
        </CardContent>
      </Card>

      {/* Active Battles */}
      {activeBattles.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-green-500" />Active Battles</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {activeBattles.map(b => (
              <div key={b.battle_id} className="flex items-center justify-between p-3 rounded-xl bg-green-500/5 border border-green-500/20">
                <div>
                  <p className="font-medium text-sm">{b.title}</p>
                  <p className="text-xs text-muted-foreground">{b.duration_minutes} min &middot; Tables: {(b.tables || []).slice(0,5).join(", ")}x{(b.tables || []).length > 5 ? "..." : ""}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => viewBattleResults(b.battle_id)} data-testid={`view-battle-${b.battle_id}`}>Results</Button>
                  <Button size="sm" variant="destructive" onClick={() => endBattle(b.battle_id)} data-testid={`end-battle-${b.battle_id}`}>End</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Battle History */}
      {battleHistory.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4" />Battle History</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {battleHistory.map(b => (
              <div key={b.battle_id} className="p-3 rounded-xl bg-muted/30 cursor-pointer hover:bg-muted/50 transition-all"
                onClick={() => viewBattleResults(b.battle_id)} data-testid={`history-${b.battle_id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm">{b.title}</p>
                  <span className="text-xs text-muted-foreground">{b.participant_count} students</span>
                </div>
                {b.top_3?.length > 0 && (
                  <div className="flex gap-3 mt-1">
                    {b.top_3.map((p, i) => (
                      <span key={i} className="text-xs text-muted-foreground">{["🥇","🥈","🥉"][i]} {p.name}: {p.score}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Assign Tables */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4" />Assign Focus Tables</CardTitle>
          <CardDescription>Select which times tables students should practice in the Arena</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-6 sm:grid-cols-11 gap-2">
            {[2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(t => (
              <button key={t} onClick={() => toggleTable(t)} data-testid={`assign-table-${t}`}
                className={`h-10 rounded-lg font-bold text-sm transition-all ${assignedTables.includes(t) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{t}x</button>
            ))}
          </div>
          <Button onClick={saveAssignment} size="sm" data-testid="save-assignment-btn">Save Assignment</Button>
        </CardContent>
      </Card>

      {/* Weekly Leaderboard with Reset */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4" />Weekly Arena Board</CardTitle>
            <Button variant="outline" size="sm" onClick={resetWeekly} data-testid="reset-weekly-btn"><RotateCcw className="w-3 h-3 mr-1" />Reset</Button>
          </div>
        </CardHeader>
        <CardContent>
          {weeklyBoard.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No activity this week</p> : (
            <div className="space-y-2">
              {weeklyBoard.map((e, i) => (
                <div key={e.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-300" : i === 2 ? "bg-orange-400 text-white" : "bg-muted"}`}>{i + 1}</span>
                  <div className="flex-1"><p className="font-medium text-sm">{e.name}</p><p className="text-xs text-muted-foreground">{e.rank} - {e.weekly_sessions} sessions</p></div>
                  <div className="text-right"><p className="font-bold text-sm text-green-500">{e.weekly_correct}</p><p className="text-xs text-muted-foreground">correct</p></div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Student Stats */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Student Arena Stats</CardTitle></CardHeader>
        <CardContent>
          {students.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No students have played yet</p> : (
            <div className="space-y-2">
              {students.map(s => (
                <div key={s.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                  <div className="flex-1"><p className="font-medium text-sm">{s.name}</p><p className="text-xs text-muted-foreground">{s.rank} - {s.total_sessions} sessions</p></div>
                  <p className="text-sm font-mono">{s.avg_speed_ms ? `${(s.avg_speed_ms / 1000).toFixed(1)}s` : "-"}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function ClassDetailInner() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [classData, setClassData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    fetchData();
  }, [classId]);

  const fetchData = async () => {
    try {
      const [classRes, docsRes, quizzesRes, announcementsRes, assignmentsRes, certsRes] = await Promise.all([
        axios.get(`${API}/classes/${classId}`, { headers }),
        axios.get(`${API}/documents?class_id=${classId}`, { headers }),
        axios.get(`${API}/quizzes?class_id=${classId}`, { headers }),
        axios.get(`${API}/announcements?class_id=${classId}`, { headers }),
        axios.get(`${API}/assignments?class_id=${classId}`, { headers }),
        axios.get(`${API}/certificates?class_id=${classId}`, { headers })
      ]);

      setClassData(classRes.data);
      setDocuments(docsRes.data);
      setQuizzes(quizzesRes.data);
      setAnnouncements(announcementsRes.data);
      setAssignments(assignmentsRes.data);
      setCertificates(certsRes.data);

      if (isTeacher) {
        const studentsRes = await axios.get(`${API}/classes/${classId}/students`, { headers });
        setStudents(studentsRes.data);
      } else {
        const submissions = {};
        for (const assignment of assignmentsRes.data) {
          try {
            const subRes = await axios.get(`${API}/assignments/${assignment.assignment_id}/my-submission`, { headers });
            if (subRes.data.submitted) {
              submissions[assignment.assignment_id] = subRes.data;
            }
          } catch (e) {
            // No submission
          }
        }
        setAssignmentSubmissions(submissions);
      }
    } catch (error) {
      toast.error("Failed to load class data");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(classData?.join_code);
    setCopiedCode(true);
    toast.success("Join code copied!");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="class-detail">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center min-h-[4rem] py-2 gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(isTeacher ? "/teacher" : "/student")}
              data-testid="back-btn"
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-foreground font-['Manrope'] leading-tight">{classData?.name}</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">{classData?.subject}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isTeacher && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyCode}
                  className="gap-2 hidden sm:flex"
                  data-testid="copy-join-code-btn"
                >
                  {copiedCode ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {classData?.join_code}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/leaderboard/${classId}`)}
                className="gap-1"
                data-testid="class-leaderboard-btn"
              >
                <Trophy className="w-4 h-4" />
                <span className="hidden sm:inline">Leaderboard</span>
              </Button>
            </div>
          </div>
          {isTeacher && (
            <div className="flex sm:hidden pb-2 pl-11">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyCode}
                className="gap-2"
                data-testid="copy-join-code-mobile-btn"
              >
                {copiedCode ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {classData?.join_code}
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Meeting Link Banner */}
        {classData?.meet_link && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <Video className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-foreground">Meeting Link Available</p>
                <p className="text-sm text-muted-foreground">Join the class online</p>
              </div>
            </div>
            <Button
              onClick={() => window.open(classData.meet_link, '_blank')}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="join-meet-btn"
            >
              <Video className="w-4 h-4 mr-2" />
              Join Meeting
            </Button>
          </div>
        )}

        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-lg w-full overflow-x-auto flex justify-start">
            <TabsTrigger value="documents" className="rounded-md" data-testid="tab-documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="quizzes" className="rounded-md" data-testid="tab-quizzes">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Quizzes
            </TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-md" data-testid="tab-announcements">
              <Bell className="w-4 h-4 mr-2" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="assignments" className="rounded-md" data-testid="tab-assignments">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Assignments
            </TabsTrigger>
            <TabsTrigger value="practice" className="rounded-md" data-testid="tab-practice">
              <Gamepad2 className="w-4 h-4 mr-2" />
              Practice
            </TabsTrigger>
            {isTeacher && (
              <TabsTrigger value="students" className="rounded-md" data-testid="tab-students">
                <Users className="w-4 h-4 mr-2" />
                Students
              </TabsTrigger>
            )}
            {isTeacher && (
              <TabsTrigger value="certificates" className="rounded-md" data-testid="tab-certificates">
                <Award className="w-4 h-4 mr-2" />
                Certificates
              </TabsTrigger>
            )}
            {isTeacher && (
              <TabsTrigger value="arena" className="rounded-md" data-testid="tab-arena">
                <Zap className="w-4 h-4 mr-2" />
                Arena
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="documents">
            <DocumentsTab
              classId={classId}
              documents={documents}
              students={students}
              isTeacher={isTeacher}
              user={user}
              headers={headers}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="quizzes">
            <QuizzesTab
              classId={classId}
              quizzes={quizzes}
              isTeacher={isTeacher}
              headers={headers}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="announcements">
            <AnnouncementsTab
              classId={classId}
              announcements={announcements}
              isTeacher={isTeacher}
              headers={headers}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="practice">
            <PracticeTab classId={classId} isTeacher={isTeacher} headers={headers} />
          </TabsContent>

          <TabsContent value="assignments">
            <AssignmentsTab
              classId={classId}
              assignments={assignments}
              assignmentSubmissions={assignmentSubmissions}
              students={students}
              isTeacher={isTeacher}
              headers={headers}
              onRefresh={fetchData}
            />
          </TabsContent>

          {isTeacher && (
            <TabsContent value="students">
              <div className="space-y-6">
                <JoinRequestsCard
                  classId={classId}
                  classData={classData}
                  headers={headers}
                  onUpdate={fetchData}
                />
                <StudentManagementCard
                  classId={classId}
                  classData={classData}
                  students={students}
                  headers={headers}
                  onUpdate={fetchData}
                />
              </div>
            </TabsContent>
          )}

          {isTeacher && (
            <TabsContent value="certificates">
              <CertificatesTab
                classId={classId}
                certificates={certificates}
                students={students}
                headers={headers}
                onRefresh={fetchData}
              />
            </TabsContent>
          )}

          {isTeacher && (
            <TabsContent value="arena">
              <ArenaTeacherTab classId={classId} headers={headers} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}


export default function ClassDetail() {
  return (
    <ClassDetailErrorBoundary>
      <ClassDetailInner />
    </ClassDetailErrorBoundary>
  );
}
